<script lang="ts" setup>
import { BaseSheetComponent } from "#components";
import { Delta, Quill, QuillEditor } from "@vueup/vue-quill";
import "@vueup/vue-quill/dist/vue-quill.snow.css";
import debounce from "lodash.debounce";
import { storeToRefs } from "pinia";
import { useUserQuickComments } from "~/stores/useUserQuickComments";
import AudioBlot from "../../blots/voice_memo_blot";

const props = defineProps({
  toolbarId: {
    type: String,
    default: "quill-toolbar",
  },
  modelValue: {
    type: Object as PropType<any>,
  },
  documentId: {
    type: String,
    required: false,
  },
  autoFocus: {
    type: Boolean,
    default: false,
  },
  quickCommentsList: {
    type: Array as PropType<string[]>,
    required: false,
  },
  showCommentsList: {
    type: Boolean,
    default: false,
  },

  readOnly: {
    type: Boolean,
    default: false,
  },

  enableSaveQuickComments: {
    type: Boolean,
    default: true,
  },
  showFormattingOptions: {
    type: Boolean,
    default: true,
  },
});

const documentId = props.documentId ?? (inject("documentId") as any);

const emits = defineEmits([
  "update:modelValue",
  "onSave",
  "onClose",
  "onSelectCommentFromList",
]);

Quill.register(AudioBlot);

const editor = ref<Quill | undefined>();

onMounted(() => {
  // const quillEditor = editor.value.quill;
  // quillEditor.container.addEventListener("keyup", onKeyUp);
  initializeQuillEvents();
});

const content = ref();
const isListening = ref(false);

const onQuillReady = (ev: any) => {
  initializeQuillEvents();

  content.value = props.modelValue ? new Delta(props.modelValue) : null;

  if (props.autoFocus) {
    setTimeout(() => {
      moveCursorToEnd();
    }, 500);
  }

  addClipboardHandler();
};

const addClipboardHandler = () => {
  if (isListening.value) {
    return;
  }

  const quillEditor = editor.value;

  if (!quillEditor) {
    return;
  }
  // getQuill();
  const quill = quillEditor.getQuill();

  isListening.value = true;

  quill.clipboard.addMatcher("*", (node: HTMLElement, delta: any) => {
    const text = node.innerText || ""; // Extracts plain text
    return new Delta().insert(text);
  });
};

const initializeQuillEvents = () => {
  if (!editor.value) {
    return;
  }

  editor.value.editor.setAttribute("tabindex", 0);
  editor.value.editor.addEventListener("keydown", onKeyUp);
};

onBeforeUnmount(() => {
  // Clean up by removing the event listener before the component is destroyed
  // const quillEditor = editor.value;
  // quillEditor.removeEventListener("keydown", onKeyUp);
});

const addVoiceMemo = (event: any) => {
  const quillEditor = editor.value;

  if (!quillEditor) {
    return;
  }
  // getQuill();
  const quill = quillEditor.getQuill();

  const range = quill.getSelection(true);

  if (!range) {
    return;
  }

  quill.insertText(range.index, "\n", Quill.sources.USER);

  quill.insertEmbed(
    range.index + 1,
    "audio",
    {
      url: event.fileUrl,
      name: event.fileName,
      alt: "Audio Recording",
    },
    Quill.sources.USER
  );

  quill.setSelection(range.index + 2, Quill.sources.SILENT);
};

const onSelected = (quickComment: QuickComment) => {
  insertText(quickComment.comment);
};

const onMultiSelected = (quickComments: QuickComments) => {
  for (const quickComment of quickComments) {
    insertText(quickComment.comment);
  }
};

const insertText = (text: string, includeLineBreak: boolean = true) => {
  const quillEditor = editor.value;
  if (!quillEditor) return;

  const quill = quillEditor.getQuill();
  let range = quill.getSelection(true);

  // If no selection, insert at the end of the document
  let insertIndex: number;
  if (range) {
    insertIndex = range.index;
  } else {
    // -1 to avoid inserting after the trailing newline Quill always keeps
    insertIndex = Math.max(0, quill.getLength() - 1);
  }

  quill.insertText(insertIndex, text, Quill.sources.USER);

  if (includeLineBreak) {
    quill.insertText(insertIndex + text.length, "\n", Quill.sources.USER);
  }

  // Optionally, only set selection if there was an active range
  if (range) {
    quill.setSelection(insertIndex + text.length, Quill.sources.SILENT);
  }
};

const highlightSelectionYellow = () => {
  const quillEditor = editor.value;
  if (!quillEditor) return;

  const quill = quillEditor.getQuill();
  const range = quill.getSelection();

  if (range && range.length > 0) {
    quill.formatText(
      range.index,
      range.length,
      "background",
      "#ffff00",
      Quill.sources.USER
    );
    return;
  }

  quill.format("background", "#ffff00", Quill.sources.USER);
};

const moveCursorToEnd = () => {
  const quillEditor = editor.value;

  if (!quillEditor) {
    return;
  }
  // getQuill();
  const quill = quillEditor.getQuill();
  const range = quill.getSelection(true);

  if (!range) {
    return;
  }

  const length = quill.getLength();
  quill.setSelection(length, 0, Quill.sources.SILENT);
};

const quickCommentsStore = useUserQuickComments();
const { quickComments } = storeToRefs(quickCommentsStore);

const editorContainer = ref();

const multiSelect = ref<HTMLElement | undefined>(undefined);

const filterComments = (option: QuickComment, select$: any) => {
  return option;
};

const onKeyUp = (ev: KeyboardEvent) => {
  if (ev.key == "s" && (ev.ctrlKey || ev.metaKey)) {
    emits("onSave");
    ev.preventDefault();
    ev.stopPropagation();
    return;
  }

  if (ev.key == "Escape") {
    emits("onClose");
    ev.preventDefault();
    ev.stopPropagation();
    return;
  }
};

const toolbar = props.readOnly ? ref({}) : ref(`#${props.toolbarId}`);

const checklistStore = useDocumentChecklistStore(documentId);
const { useChecklist } = storeToRefs(checklistStore);

const submittedDocumentStore = useSubmittedDocumentStore(documentId);
const { submittedDocument } = storeToRefs(submittedDocumentStore);

const filteredQuickComments = computed(() => {
  const lesson =
    submittedDocument.value?.assignmentLessonId ?? useChecklist.value?.lessonId;
  const level =
    submittedDocument.value?.assignmentLevelId ?? useChecklist.value?.levelId;

  const filteredComments = quickComments.value.filter((quickComment) => {
    const matchingLevel =
      !level ||
      !quickComment.levels ||
      quickComment.levels.length == 0 ||
      quickComment.levels.includes(level);

    const matchingLesson =
      !lesson ||
      !quickComment.lessons ||
      quickComment.lessons.length == 0 ||
      quickComment.lessons.includes(lesson);

    return matchingLevel && matchingLesson;
  });

  return filteredComments;
});

const onKeyDown = (event: KeyboardEvent) => {
  // if (event.shiftKey && event.key === '"') {
  //   event.preventDefault();
  //   formatQuotes();
  // } else if (event.key == "Backspace" || event.key == "Delete") {
  //   checkQuotes(event);
  // } else if (event.key === "Æ" && event.altKey) {
  //   event.preventDefault();
  //   formatQuotes(""");
  // }

  if (event.key == " ") {
    checkEmoji();
  }

  if (event.key === "Backspace") {
    handleBackspaceAlignment(event);
  }
};

const handleBackspaceAlignment = (event: KeyboardEvent) => {
  const quillEditor = editor.value;
  if (!quillEditor) return;

  const quill = quillEditor.getQuill();
  const selection = quill.getSelection();

  if (!selection || selection.length > 0) return; // Only handle if cursor, not selection

  const [line, offset] = quill.getLine(selection.index);

  // Check if cursor is at the start of the line
  if (offset === 0) {
    const format = quill.getFormat(selection.index);

    // Check if the line is centered
    if (format.align === "center") {
      event.preventDefault();
      // Remove center alignment (defaults to left)
      quill.formatLine(selection.index, 1, "align", false);
    }
  }
};

const checkEmoji = () => {
  const quillEditor = editor.value;

  if (!quillEditor) {
    return;
  }
  // getQuill();
  const quill = quillEditor.getQuill();

  const selection = quill.getSelection();
  if (!selection) return;
  const [line, offset] = quill.getLine(selection.index);

  const textBefore = line.domNode.textContent.substring(offset - 2, offset);
  const matchingEmoji = emojiShortcuts.value[textBefore];

  if (matchingEmoji) {
    // Delete the range of text
    quill.deleteText(selection.index - 2, 2);

    // Insert new text (emoji in this case) at the start index
    quill.insertText(selection.index - 2, matchingEmoji);
  }
};

// const onCheckQuotes = () => {
//   var quillEditor = editor.value;

//   if (!quillEditor) {
//     return;
//   }

//   const quill = quillEditor.getQuill();
//   const text = quill.getText();
//   const length = text.length;

//   let cursor = 0;
//   while (cursor < length) {
//     const quoteIndex = text.indexOf('"', cursor);
//     if (quoteIndex === -1) break; // No more quotes found

//     // Determine if the quote should be open or close
//     const isOpenQuote = quoteIndex === 0 || /\s/.test(text[quoteIndex - 1]);

//     // Create the delta for the replacement
//     const delta = new Delta()
//       .retain(quoteIndex) // Retain all text before the quote
//       .delete(1) // Delete the quote
//       .insert(isOpenQuote ? "“" : "”"); // Insert the correct quote

//     // Apply the delta
//     quill.updateContents(delta);

//     // Move cursor forward
//     cursor = quoteIndex + 1;
//   }
// };

const onCheckQuotes = () => {
  const quillEditor = editor.value;

  if (!quillEditor) {
    return;
  }

  const quill = quillEditor.getQuill();
  const text = quill.getText();
  const length = text.length;

  let cursor = 0;
  while (cursor < length) {
    // Find the index of the next quote (straight, open, or close)
    const quoteIndex = text.substring(cursor).search(/["“”]/);
    if (quoteIndex === -1) break; // No more quotes found

    const absoluteIndex = cursor + quoteIndex;

    const replacementQuote = useGetSmartQuoteReplacement(text, absoluteIndex);

    if (replacementQuote) {
      // Create the delta for the replacement
      const delta = new Delta()
        .retain(absoluteIndex) // Retain all text before the quote
        .delete(1) // Delete the current quote
        .insert(replacementQuote); // Insert the correct quote

      // Apply the delta
      quill.updateContents(delta);
    }

    // Move cursor forward
    cursor = absoluteIndex + 1;
  }
};

const selectedText = ref<string | undefined>(undefined);
const onSelectionChange = (event: any) => {
  const quillEditor = editor.value;
  const range = event.range;

  if (range && quillEditor) {
    selectedText.value = quillEditor.getText(range.index, range.length);
  } else {
    selectedText.value = undefined;
  }
};

const onSelectionChangedDebounced = debounce(onSelectionChange, 200);
const saveQuickComment = () => {
  const nuxtApp = useNuxtApp();

  const textToSave =
    selectedText.value != undefined && selectedText.value != ""
      ? selectedText.value
      : // @ts-ignore
        nuxtApp.$convertQuillDeltaToPlaintext(props.modelValue);

  const { openModal } = useSaveAsQuickComment(documentId, textToSave);
  openModal();
};

const onCommentClicked = (event: any) => {
  emits("onSelectCommentFromList", event);
  insertText(event + " ");
};

const emojis = ref(["😀", "😊", "😲", "👍", "👏", "🎉", "🎯", "💯", "←", "→", "⭐"]);

const emojiShortcuts = ref<{ [key: string]: string }>({
  ":)": "😀",

  ":3": "😊",

  ":>": "😆",
  ":^)": "😆",
  "->": "→",
  "<-": "←",
  "=>": "→",
  "<=": "←",
});

const onClick = () => {
  if (props.readOnly) return;

  const quillEditor = editor.value;
  quillEditor.focus();
};

const sheetController = useNewSheetController();
const newComment = ref<QuickComment | undefined>(undefined);
const { createDefaultComment } = useAdminQuickComments();

const saveComment = () => {
  const nuxtApp = useNuxtApp();

  const textToSave =
    selectedText.value != undefined && selectedText.value != ""
      ? selectedText.value
      : // @ts-ignore
        nuxtApp.$convertQuillDeltaToPlaintext(props.modelValue);

  const quickComment = createDefaultComment();
  quickComment.comment = textToSave;

  const submittedDocumentStore = useSubmittedDocumentStore(documentId);
  const { documentLevel, documentLesson } = storeToRefs(submittedDocumentStore);

  if (documentLevel.value) {
    quickComment.levels = [documentLevel.value];
  }

  if (documentLesson.value) {
    quickComment.lessons = [documentLesson.value];
  }

  newComment.value = quickComment;

  sheetController.value.open();
};

const isFocused = ref(false);
const onFocus = () => {
  isFocused.value = true;
};

const onBlur = () => {
  isFocused.value = false;
};
</script>

<template>
  <div class="flex flex-col">
    <div
      v-if="quickComments && !readOnly"
      class="my-2"
      data-step-id="quick-comments-autocomplete-container"
    >
      <QuickCommentAutocomplete
        :data="filteredQuickComments"
        placeholder="Search for quick comments..."
        @on-selected="onSelected"
        @on-multi-selected="onMultiSelected"
        @on-insert-text="insertText"
      />
    </div>
    <CommentList
      v-if="
        showCommentsList && quickCommentsList && quickCommentsList.length > 0
      "
      :comments="quickCommentsList"
      class="mb-2"
      @selected="onCommentClicked"
    />

    <div
      v-if="!readOnly"
      :id="toolbarId"
      class="border !border-medium-tint rounded mb-2 flex flex-row items-center justify-between w-full"
    >
      <div class="flex flex-row items-center">
        <button v-if="showFormattingOptions" class="ql-bold" />
        <button v-if="showFormattingOptions" class="ql-italic" />
        <button v-if="showFormattingOptions" class="ql-underline" />
        <button v-if="showFormattingOptions" class="ql-strike" />
        <button
          v-if="showFormattingOptions"
          class="inline-flex h-[24px] w-[28px] items-center justify-center text-text hover:text-focus"
          aria-label="Highlight yellow"
          title="Highlight yellow"
          @mousedown.prevent.stop="highlightSelectionYellow"
        >
          <icon name="mdi:format-color-highlight" />
        </button>
        <BasePopover
          side="top"
          trigger-class="inline-flex items-center justify-center align-middle"
        >
          <span
            class="inline-flex items-center justify-center align-middle h-[12px]"
          >
            😊
          </span>
          <template #content>
            <div class="bg-surface grid grid-cols-6 p-1 justify-start">
              <BaseIconButton
                v-for="(emoji, index) in emojis"
                :key="index"
                @click="insertText(emoji, false)"
              >
                {{ emoji }}
              </BaseIconButton>
            </div>
          </template>
        </BasePopover>
      </div>
      <div class="flex flex-row justify-end grow">
        <BaseSheetComponent
          width="700px"
          :controller="sheetController"
          :includeContentPadding="false"
          :z-index-override="102"
        >
          <template #trigger>
            <BaseIconButton
              tooltip="Save to Quick Comments"
              data-step-id="add-quick-comment-button"
              class="!p-[2px]"
              @click.stop.prevent="saveComment()"
            >
              <icon name="material-symbols:add-comment" />
            </BaseIconButton>
          </template>
          <template #content>
            <UserQuickCommentsForm
              v-if="newComment"
              :quickComment="newComment"
              :is-in-modal="true"
              @saved="sheetController.close()"
            />
          </template>
        </BaseSheetComponent>
      </div>
    </div>
    <div
      ref="editorContainer"
      :class="{
        'border border-medium-tint rounded grow min-h-[200px] overflow-auto cursor-text':
          !readOnly,
        'outline outline-focus outline-2 -outline-offset-1': isFocused,
      }"
      @click="onClick"
    >
      <QuillEditor
        ref="editor"
        :toolbar="toolbar"
        theme="snow"
        :options="{
          bounds: editorContainer,
          formats: [
            'bold',
            'italic',
            'underline',
            'strike',
            'background',
            'link',
          ],
        }"
        :content="content"
        :read-only="readOnly"
        :enable="!readOnly"
        @update:content="$emit('update:modelValue', $event.ops)"
        @ready="onQuillReady"
        @keydown="onKeyDown"
        @keyup="onCheckQuotes"
        @selection-change="onSelectionChangedDebounced"
        @focus="onFocus"
        @blur="onBlur"
      />
    </div>
  </div>
</template>

<style>
.ql-toolbar.ql-snow + .ql-container.ql-snow {
  border-top: unset;
}

.ql-container.ql-snow {
  border: unset;
}

.ql-container,
.ql-editor {
}

.ql-editor {
  border: unset;
}
</style>
